import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  enableCompanyDocumentInAva,
  fetchCompanyDocumentBlob,
  getPdfContentType,
  requestCompanyDocumentFileReplacement,
  restorePreviousCompanyDocumentFile,
  uploadCompanyDocumentFile,
} from "../company-document-api";
import { setCompanyDocumentInCache } from "../company-document-query-cache";
import { COMPANY_DOCUMENTS_QUERY_KEY } from "../constants";

import type { ReplaceCompanyDocumentValues } from "../types";

export function useReplaceCompanyDocumentFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ document, file }: ReplaceCompanyDocumentValues) => {
      const previousFile = await fetchCompanyDocumentBlob(document.id);
      const upload = await requestCompanyDocumentFileReplacement({
        contentType: getPdfContentType(file),
        documentId: document.id,
        filename: file.name,
        fileSizeBytes: file.size,
      });

      try {
        await uploadCompanyDocumentFile(file, upload);

        if (document.useInAva) {
          const updatedDocument = await enableCompanyDocumentInAva(document.id);

          return updatedDocument;
        }
      } catch (error) {
        await restorePreviousCompanyDocumentFile(document, previousFile);

        throw error;
      }
    },
    onSuccess: (document) => {
      if (document) {
        setCompanyDocumentInCache(queryClient, document);
      }
    },
    onSettled: (document, error) => {
      if (!document || error) {
        return queryClient.invalidateQueries({ queryKey: COMPANY_DOCUMENTS_QUERY_KEY });
      }
    },
  });
}
